import React, { useContext } from 'react';
import { Button } from '@thd-olt-component-react/core-ui';
import { useNavigate } from '@thd-olt-component-react/router';
import { clickEvents } from '../../../../utils/analytics';
import { EXP_ROUTES, JOB_POSTING_STATUS } from '../../../../utils/constants';
import { JobPostingContext } from '../../../../contexts/JobPostingContext';
import { useStore } from '../../../../store';
import { composeGoBackUrlParam } from '../../../../utils/go-back';

function buttonWithContext(BaseComp) {
  return function ButtonWithContext() {
    const {
      jobPostingStore: {
        toggleMarkCompleteModal,
        toggleCancelJobPostingModal,
        toggleReopenJobPostingModal,
        toggleDeleteJobPostingModal,
        togglePublishJobPostingModal,
        copyJobPosting
      }
    } = useStore();

    const {
      editorModeActive,
      toggleEditorMode,
      activeJobPosting,
      saveJobPosting,
      canPublishJobPosting,
      getJobPosting
    } = useContext(JobPostingContext);

    const props = {
      editorModeActive,
      toggleEditorMode,
      activeJobPosting,
      saveJobPosting,
      canPublishJobPosting,
      getJobPosting,
      toggleMarkCompleteModal,
      toggleCancelJobPostingModal,
      toggleReopenJobPostingModal,
      toggleDeleteJobPostingModal,
      togglePublishJobPostingModal,
      copyJobPosting
    };

    return <BaseComp {...props} />;
  };
}

export const DeleteJobPosting = buttonWithContext(
  ({ toggleDeleteJobPostingModal, activeJobPosting }) => {
    return (
      <Button
        className="ptp-job-posting__side-bar-button"
        onClick={() => {
          clickEvents.postingDetailsModifications('delete');
          toggleDeleteJobPostingModal(activeJobPosting);
        }}
        dark
        data-testid="open-delete-job-posting-modal-jpp"
        outline
        style={{ fontWeight: 700 }}
      >
        Delete Posting
      </Button>
    );
  }
);

export const ReopenJobPosting = buttonWithContext(
  ({ toggleReopenJobPostingModal, activeJobPosting }) => {
    return (
      <Button
        className="ptp-job-posting__side-bar-button"
        onClick={() => {
          clickEvents.postingDetailsModifications('reopen');
          toggleReopenJobPostingModal(activeJobPosting.jobPostingId);
        }}
        data-testid="open-reopen-job-posting-modal-jpp"
        dark
        outline
        style={{ fontWeight: 700 }}
      >
        Re-Open Posting
      </Button>
    );
  }
);

export const CancelJobPosting = buttonWithContext(
  ({ toggleCancelJobPostingModal, activeJobPosting }) => {
    return (
      <Button
        className="ptp-job-posting__side-bar-left"
        onClick={() => {
          clickEvents.postingDetailsModifications('cancel');
          toggleCancelJobPostingModal(activeJobPosting.jobPostingId);
        }}
        data-testid="open-cancel-job-posting-modal-jpp"
        dark
        outline
        style={{ fontWeight: 700 }}
      >
        Cancel Posting
      </Button>
    );
  }
);

export const EditorModeButtons = buttonWithContext(({ toggleEditorMode, activeJobPosting }) => {
  const navigate = useNavigate();
  return (
    <div>
      <PublishJobPosting />
      <SaveAsDraft />
      <Button
        className="ptp-job-posting__side-bar-button-space-right"
        onClick={() => {
          toggleEditorMode();
          navigate(EXP_ROUTES.JOB_POSTING(activeJobPosting.jobPostingId));
        }}
        data-testid="open-publish-job-posting-modal-jpp"
        outline
        dark
        style={{ fontWeight: 700 }}
      >
        Discard Changes
      </Button>
    </div>
  );
});

export const PublishJobPosting = buttonWithContext(
  ({ togglePublishJobPostingModal, activeJobPosting, editorModeActive }) => {
    return (
      <Button
        className="ptp-job-posting__side-bar-button-space-right"
        outline={!editorModeActive}
        onClick={() => {
          clickEvents.postingDetailsModifications('publish');
          togglePublishJobPostingModal(activeJobPosting);
        }}
        data-testid="open-publish-job-posting-modal-jpp"
        style={{ fontWeight: 700 }}
      >
        {activeJobPosting.status === JOB_POSTING_STATUS.ACTIVE ? 'Update Posting' : 'Publish Posting'}
      </Button>
    );
  });

export const SaveAsDraft = buttonWithContext(({ saveJobPosting, activeJobPosting }) => {
  return (
    <Button
      className="ptp-job-posting__side-bar-button"
      onClick={() => {
        clickEvents.postingDetailsModifications('save as draft');
        saveJobPosting(false);
      }}
      data-testid="open-complete-job-posting-modal-jpp"
      outline
      style={{ fontWeight: 700 }}
    >
      {activeJobPosting.status === JOB_POSTING_STATUS.ACTIVE ? 'Revert to Draft' : 'Save as Draft'}
    </Button>
  );
});

export const DuplicateJobPosting = buttonWithContext(
  ({ getJobPosting, copyJobPosting, activeJobPosting, toggleEditorMode }) => {

    const navigate = useNavigate();

    const setNewJobId = (newJobId) => {
      getJobPosting(newJobId);
      navigate(`${EXP_ROUTES.JOB_POSTING(newJobId)}${composeGoBackUrlParam()}`);
      toggleEditorMode();
    };

    return (
      <Button
        className="ptp-job-posting__side-bar-button-space-right"
        outline
        onClick={() => {
          copyJobPosting(activeJobPosting.jobPostingId, setNewJobId);
        }}
        data-testid="duplicate-job-posting-button"
        style={{ fontWeight: 700 }}
      >
      Duplicate Job Posting
      </Button>
    );
  }
);

export const MarkCompleteJobPosting = buttonWithContext(
  ({ toggleMarkCompleteModal, activeJobPosting }) => {
    return (
      <Button
        className="ptp-job-posting__side-bar-button"
        onClick={() => {
          clickEvents.postingDetailsModifications('mark complete');
          toggleMarkCompleteModal(activeJobPosting.jobPostingId);
        }}
        data-testid="open-complete-job-posting-modal-jpp"
        dark
        outline
        style={{ fontWeight: 700 }}
      >
        Mark Complete
      </Button>
    );
  }
);

export const EditJobPosting = buttonWithContext(({ toggleEditorMode }) => {
  return (
    <Button
      className="ptp-job-posting__side-bar-button"
      onClick={() => {
        clickEvents.postingDetailsModifications('edit');
        toggleEditorMode();
      }}
      style={{ fontWeight: 700 }}
    >
      Edit Posting
    </Button>
  );
});
